import React, { useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import { AlineButton, AlineTooltip, AnimatedAlineDialog } from 'src/components/generic';
import { PersonIcon } from 'src/components/icons/PersonIcon';
import type { RoleOption } from 'src/components/RolesAutocomplete';
import { RolesAutoComplete } from 'src/components/RolesAutocomplete';
import type { WorkflowCollaborator } from 'src/components/workflow';
import { Apollo } from 'src/gql/generated';
import { useGetRoleFromOption, useGetWorkflowPermissions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

export const useCollabDialogStyles = makeStyles()(theme => ({
  formSubmitButtons: {
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
    width: '100%',
  },
  nav: {
    alignItems: 'center',
    display: 'flex',
    height: 48,
    marginBottom: theme.spacing(2),
  },
  paper: {
    width: theme.dialogStyles.standardDialogWidth,
  },
  rightNav: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexGrow: 1,
    height: '100%',
    justifyContent: 'flex-end',
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  userContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ChangeCollaboratorRoleDialogProps {
  collaborator: Pick<WorkflowCollaborator, 'id' | 'role' | '__typename'>;
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}

export function ChangeCollaboratorRoleDialog({
  collaborator,
  onClose,
  open,
  workflowId,
}: ChangeCollaboratorRoleDialogProps): JSX.Element {
  const { classes } = useCollabDialogStyles();
  const { getRoleFromOption } = useGetRoleFromOption();
  const { permissions } = useGetWorkflowPermissions(workflowId);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [roleOption, setRoleOption] = useState<RoleOption | null>(collaborator.role);
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const dirty = roleOption?.__typename === 'NewRole' || collaborator.role?.id !== roleOption?.id;
  const disabledReason = !dirty ? 'Change role to edit' : '';

  useEffect(() => {
    setRoleOption(collaborator.role);
  }, [collaborator.role]);

  function resetState(): void {
    setIsSubmitting(false);
  }

  return (
    <AnimatedAlineDialog
      classes={{ paper: classes.paper }}
      open={open}
      title={
        <div className={classes.title}>
          <PersonIcon />
          <Typography variant="h1">Edit Role</Typography>
        </div>
      }
      onClose={onClose}
    >
      <RolesAutoComplete
        getIsOptionDisabled={option => {
          if (option.__typename === 'NewRole') return false;

          const roleAlreadyUsedInWF = permissions.some(p => p.role?.id === option.id);
          return roleAlreadyUsedInWF;
        }}
        value={collaborator.role}
        onChange={(_, option) => {
          setRoleOption(option);
        }}
      />
      <div className={classes.formSubmitButtons}>
        <AlineButton
          color="primary"
          disabled={isSubmitting}
          variant="outlined"
          onClick={() => {
            onClose();
            resetState();
          }}
        >
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={isSubmitting}
              variant="contained"
              onClick={async () => {
                setIsSubmitting(true);
                const { assignedRoleId } = await getRoleFromOption(roleOption);
                await changeCollaboratorAssignment({
                  variables: {
                    input: {
                      assignedRoleId,
                      permissionId: collaborator.id,
                    },
                  },
                });
                onClose();
                resetState();
              }}
            >
              Save
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </AnimatedAlineDialog>
  );
}
